import React, { useState, useEffect, useRef, useCallback } from 'react';
import { syslogAPI, dotLogsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import './Syslog.css';
import DateInput from '../../components/DateInput/DateInput';
import { exportTableToExcel } from '../../utils/excel';

// An export's table (the server's columns, format=json) saved as an Excel file.
function saveTable(request, filename, sheetName) {
    request
        .then((res) => exportTableToExcel(filename, res.data, { sheetName }))
        .catch(() => toast.error('Export failed'));
}
const filled = (params) => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined));

// ── Constants ────────────────────────────────────────────────
const SEV_COLOR = {
    emergency: '#7c3aed', alert: '#dc2626', critical: '#dc2626',
    error: '#ef4444', warning: '#f59e0b', notice: '#3b82f6',
    info: '#10b981', debug: '#6b7280',
};
const SEV_BG = {
    emergency: '#f5f3ff', alert: '#fef2f2', critical: '#fef2f2',
    error: '#fef2f2', warning: '#fffbeb', notice: '#eff6ff',
    info: '#ecfdf5', debug: '#f9fafb',
};
const CAT_ICONS = {
    firewall:'fa-shield-alt', nat:'fa-exchange-alt', dns:'fa-globe',
    dhcp:'fa-server', hotspot:'fa-wifi', radius:'fa-broadcast-tower',
    auth:'fa-key', vpn:'fa-lock', ipsec:'fa-lock', wireguard:'fa-lock',
    system:'fa-microchip', interface:'fa-ethernet', routing:'fa-route',
    wireless:'fa-wifi', qos:'fa-sliders-h', resource:'fa-tachometer-alt',
    general:'fa-scroll',
};

function sevColor(s) { return SEV_COLOR[s] || '#6b7280'; }
function sevBg(s)    { return SEV_BG[s] || '#f9fafb'; }
function catIcon(c)  { return CAT_ICONS[c] || 'fa-scroll'; }
function fmtTime(d)  {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleTimeString('en-IN', { hour12: false }) + ' ' +
           dt.toLocaleDateString('en-IN', { day:'2-digit', month:'short' });
}
function relTime(d) {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return `${Math.floor(diff/1000)}s ago`;
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    return `${Math.floor(diff/3600000)}h ago`;
}

// ── Sev badge ────────────────────────────────────────────────
function SevBadge({ s }) {
    return (
        <span className="sl-sev-badge" style={{ background: sevBg(s), color: sevColor(s) }}>
            {s || 'unknown'}
        </span>
    );
}

// ── Metric card ──────────────────────────────────────────────
function MetricCard({ icon, label, value, color, sub }) {
    return (
        <div className="sl-metric-card">
            <div className="sl-metric-icon" style={{ background: color + '20', color }}>
                <i className={`fas ${icon}`} />
            </div>
            <div>
                <div className="sl-metric-val">{value?.toLocaleString() ?? '—'}</div>
                <div className="sl-metric-label">{label}</div>
                {sub && <div className="sl-metric-sub">{sub}</div>}
            </div>
        </div>
    );
}

// ── Mini bar ─────────────────────────────────────────────────
function MiniBar({ items, total, colorFn }) {
    if (!items?.length) return <div className="sl-no-data">No data</div>;
    return (
        <div className="sl-mini-bar-list">
            {items.slice(0, 8).map(item => {
                const pct = total ? Math.round((item.count / total) * 100) : 0;
                return (
                    <div key={item._id} className="sl-mini-bar-row">
                        <span className="sl-mini-bar-label">{item._id || 'unknown'}</span>
                        <div className="sl-mini-bar-track">
                            <div className="sl-mini-bar-fill"
                                style={{ width: `${pct}%`, background: colorFn ? colorFn(item._id) : '#6366f1' }} />
                        </div>
                        <span className="sl-mini-bar-count">{item.count?.toLocaleString()}</span>
                    </div>
                );
            })}
        </div>
    );
}

// ── Timeline ─────────────────────────────────────────────────
function Timeline({ data }) {
    if (!data?.length) return <div className="sl-no-data">No data</div>;
    const maxCount = Math.max(...data.map(d => d.count), 1);
    return (
        <div className="sl-timeline">
            {data.map((d, i) => (
                <div key={i} className="sl-timeline-col" title={`${d._id}: ${d.count} logs, ${d.threats} threats`}>
                    <div className="sl-timeline-bar-wrap">
                        <div className="sl-timeline-bar-threat"
                            style={{ height: `${(d.threats / maxCount) * 100}%` }} />
                        <div className="sl-timeline-bar-total"
                            style={{ height: `${(d.count / maxCount) * 100}%` }} />
                    </div>
                    <div className="sl-timeline-label">
                        {new Date(d._id).getHours().toString().padStart(2,'0')}h
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Log row ──────────────────────────────────────────────────
function LogRow({ log, onSelect, sr }) {
    return (
        <tr className={`sl-log-row ${log.isThreat ? 'threat' : ''}`} onClick={() => onSelect(log)}>
            <td className="sl-sr">{sr}</td>
            <td className="sl-td-time">{fmtTime(log.receivedAt)}</td>
            <td className="sl-td-nas">{log.nasIdentifier || log.nasIp}</td>
            <td className="sl-td-host">{log.hostname || '—'}</td>
            <td className="sl-td-vendor">
                <span className="sl-vendor-chip">{log.vendor || 'unknown'}</span>
            </td>
            <td className="sl-td-cat">
                <span className="sl-cat-chip">
                    <i className={`fas ${catIcon(log.category)}`} />
                    {log.category}
                </span>
            </td>
            <td><SevBadge s={log.severityName} /></td>
            <td className="sl-td-msg">
                {log.isThreat && (
                    <span className="sl-threat-chip">
                        <i className="fas fa-exclamation-triangle" /> {log.threatType}
                    </span>
                )}
                <span className="sl-msg-text">{log.message}</span>
            </td>
            <td className="sl-td-ip">{log.srcIp || '—'}</td>
        </tr>
    );
}

// ── Log detail modal ─────────────────────────────────────────
function LogDetail({ log, onClose }) {
    if (!log) return null;
    const fields = [
        ['Received At', fmtTime(log.receivedAt)],
        ['NAS IP', log.nasIp],
        ['NAS Identifier', log.nasIdentifier],
        ['Hostname', log.hostname],
        ['Vendor', log.vendor],
        ['Category', log.category],
        ['Subcategory', log.subcategory],
        ['Severity', log.severityName],
        ['Facility', log.facilityName],
        ['Source IP', log.srcIp],
        ['Dest IP', log.dstIp],
        ['Src Port', log.srcPort],
        ['Dst Port', log.dstPort],
        ['MAC', log.mac],
        ['Username', log.username],
        ['Domain', log.domain],
        ['Interface', log.iface],
        ['Threat', log.isThreat ? `YES — ${log.threatType} (score ${log.threatScore})` : 'No'],
        ['App Name', log.appName],
    ].filter(([,v]) => v);

    return (
        <div className="sl-modal-overlay" onClick={onClose}>
            <div className="sl-modal" onClick={e => e.stopPropagation()}>
                <div className="sl-modal-header">
                    <h3>Log Detail</h3>
                    <button className="sl-modal-close" onClick={onClose}><i className="fas fa-times" /></button>
                </div>
                <div className="sl-modal-body">
                    <div className="sl-modal-msg">
                        <strong>Message:</strong>
                        <pre>{log.message}</pre>
                    </div>
                    <div className="sl-modal-grid">
                        {fields.map(([k, v]) => (
                            <div key={k} className="sl-modal-field">
                                <span className="sl-modal-key">{k}</span>
                                <span className="sl-modal-val">{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── DoT dynamic table (renders CSV-derived rows) ─────────
function DotTable({ rows, loading, emptyMsg, firstSr = 1 }) {
    if (loading) return <div className="sl-no-data"><i className="fas fa-spinner fa-spin" /> Loading…</div>;
    if (!rows?.length) return <div className="sl-no-data">{emptyMsg || 'No data'}</div>;
    const cols = Object.keys(rows[0]);
    return (
        <div className="sl-table-wrap">
            <table className="sl-table">
                <thead>
                    <tr><th className="sl-sr">Sr. No.</th>{cols.map(c => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="sl-log-row">
                            <td className="sl-sr">{firstSr + i}</td>
                            {cols.map(c => <td key={c} className="sl-td-msg">{row[c] || '—'}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DotPager({ pag, onPage }) {
    if (!pag || pag.pages <= 1) return null;
    return (
        <div className="sl-pagination">
            <button disabled={pag.page <= 1} onClick={() => onPage(pag.page - 1)}>
                <i className="fas fa-chevron-left" />
            </button>
            <span>Page {pag.page} of {pag.pages}</span>
            <button disabled={pag.page >= pag.pages} onClick={() => onPage(pag.page + 1)}>
                <i className="fas fa-chevron-right" />
            </button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const Syslog = () => {
    const [tab, setTab] = useState('dashboard');
    const [dashboard, setDashboard] = useState(null);
    const [logs, setLogs] = useState([]);
    const [liveLogs, setLiveLogs] = useState([]);
    const [threats, setThreats] = useState({ logs: [], summary: [] });
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [liveMode, setLiveMode] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });

    // DoT compliance data
    const [dotSessions,    setDotSessions]    = useState([]);
    const [dotBrowsing,    setDotBrowsing]    = useState([]);
    const [dotRouterLogs,  setDotRouterLogs]  = useState([]);
    const [dotPagSessions, setDotPagSessions] = useState({ page: 1, total: 0, pages: 1 });
    const [dotPagBrowsing, setDotPagBrowsing] = useState({ page: 1, total: 0, pages: 1 });
    const [dotPagRouter,   setDotPagRouter]   = useState({ page: 1, total: 0, pages: 1 });
    const [dotFilters, setDotFilters] = useState({
        startDate: new Date().toISOString().slice(0, 10),
        endDate:   new Date().toISOString().slice(0, 10),
        nasIp: '',
    });
    const [dotLoading, setDotLoading] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        startDate: new Date(Date.now() - 24*3600*1000).toISOString().slice(0,16),
        endDate:   new Date().toISOString().slice(0,16),
        nasIp: '', vendor: '', category: '', severity: '',
        search: '', isThreat: '',
    });

    const liveTimerRef = useRef(null);
    const wsRef        = useRef(null);

    // ── Fetch dashboard ─────────────────────────────────────
    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const res = await syslogAPI.getDashboard({
                startDate: filters.startDate,
                endDate:   filters.endDate,
            });
            setDashboard(res.data.data);
        } catch {
            toast.error('Failed to load dashboard');
        } finally { setLoading(false); }
    }, [filters.startDate, filters.endDate]);

    // ── Fetch logs ──────────────────────────────────────────
    const fetchLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = { ...filters, page, limit: 100 };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const res = await syslogAPI.getLogs(params);
            setLogs(res.data.data || []);
            setPagination(res.data.pagination || { page, total: 0, pages: 1 });
        } catch {
            toast.error('Failed to fetch logs');
        } finally { setLoading(false); }
    }, [filters]);

    // ── Fetch live ──────────────────────────────────────────
    const fetchLive = useCallback(async () => {
        try {
            const res = await syslogAPI.getLive();
            setLiveLogs(res.data.data || []);
        } catch {}
    }, []);

    // ── Fetch threats ────────────────────────────────────────
    const fetchThreats = useCallback(async () => {
        setLoading(true);
        try {
            const res = await syslogAPI.getThreats({ startDate: filters.startDate, endDate: filters.endDate });
            setThreats(res.data.data || { logs: [], summary: [] });
        } catch { toast.error('Failed to fetch threats'); }
        finally { setLoading(false); }
    }, [filters.startDate, filters.endDate]);

    // ── Fetch alerts ─────────────────────────────────────────
    const fetchAlerts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await syslogAPI.getAlerts({ status: 'open' });
            setAlerts(res.data.data || []);
        } catch { toast.error('Failed to fetch alerts'); }
        finally { setLoading(false); }
    }, []);

    // ── DoT fetch helpers ────────────────────────────────────
    const fetchDotSessions = useCallback(async (page = 1) => {
        setDotLoading(true);
        try {
            const params = { startDate: dotFilters.startDate, endDate: dotFilters.endDate, page, limit: 100 };
            if (dotFilters.nasIp) params.nasIp = dotFilters.nasIp;
            const res = await dotLogsAPI.getSessions(params);
            setDotSessions(res.data.data || []);
            setDotPagSessions(res.data.pagination || { page, total: 0, pages: 1 });
        } catch { toast.error('Failed to fetch sessions'); }
        finally { setDotLoading(false); }
    }, [dotFilters]);

    const fetchDotBrowsing = useCallback(async (page = 1) => {
        setDotLoading(true);
        try {
            const params = { startDate: dotFilters.startDate, endDate: dotFilters.endDate, page, limit: 100 };
            if (dotFilters.nasIp) params.nasIp = dotFilters.nasIp;
            const res = await dotLogsAPI.getBrowsing(params);
            setDotBrowsing(res.data.data || []);
            setDotPagBrowsing(res.data.pagination || { page, total: 0, pages: 1 });
        } catch { toast.error('Failed to fetch browsing logs'); }
        finally { setDotLoading(false); }
    }, [dotFilters]);

    const fetchDotRouterLogs = useCallback(async (page = 1) => {
        setDotLoading(true);
        try {
            const params = { startDate: dotFilters.startDate, endDate: dotFilters.endDate, page, limit: 100 };
            if (dotFilters.nasIp) params.nasIp = dotFilters.nasIp;
            const res = await dotLogsAPI.getRouterSystem(params);
            setDotRouterLogs(res.data.data || []);
            setDotPagRouter(res.data.pagination || { page, total: 0, pages: 1 });
        } catch { toast.error('Failed to fetch router logs'); }
        finally { setDotLoading(false); }
    }, [dotFilters]);

    const setDotFilter = (k) => (e) => setDotFilters(prev => ({ ...prev, [k]: e.target.value }));

    // ── Tab change ───────────────────────────────────────────
    useEffect(() => {
        if (tab === 'dashboard') fetchDashboard();
        else if (tab === 'logs')      fetchLogs();
        else if (tab === 'live')      { fetchLive(); startLive(); }
        else if (tab === 'threats')   fetchThreats();
        else if (tab === 'alerts')    fetchAlerts();
        else if (tab === 'sessions')  fetchDotSessions();
        else if (tab === 'browsing')  fetchDotBrowsing();
        else if (tab === 'router-logs') fetchDotRouterLogs();

        return () => stopLive();
    }, [tab]); // eslint-disable-line

    // ── Live polling ─────────────────────────────────────────
    function startLive() {
        setLiveMode(true);
        liveTimerRef.current = setInterval(fetchLive, 3000);
    }
    function stopLive() {
        setLiveMode(false);
        clearInterval(liveTimerRef.current);
    }

    // ── WebSocket for real-time push ──────────────────────────
    useEffect(() => {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
        wsRef.current = ws;
        ws.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data);
                if (msg.type === 'SYSLOG_ENTRY' && tab === 'live') {
                    setLiveLogs(prev => [msg.data, ...prev].slice(0, 200));
                }
                if (msg.type === 'SYSLOG_ALERT' && tab === 'alerts') {
                    fetchAlerts();
                }
            } catch {}
        };
        return () => ws.close();
    }, [tab]); // eslint-disable-line

    // ── Alert actions ─────────────────────────────────────────
    const ackAlert = async (id) => {
        try {
            await syslogAPI.acknowledgeAlert(id);
            toast.success('Alert acknowledged');
            fetchAlerts();
        } catch { toast.error('Failed'); }
    };
    const resolveAlert = async (id) => {
        try {
            await syslogAPI.resolveAlert(id);
            toast.success('Alert resolved');
            fetchAlerts();
        } catch { toast.error('Failed'); }
    };

    const setFilter = (k) => (e) => setFilters(prev => ({ ...prev, [k]: e.target.value }));

    // ── Summary numbers ─────────────────────────────────────
    const summary = dashboard?.summary || {};
    const bySev   = dashboard?.bySeverity || [];
    const critCount  = bySev.find(s => s._id === 'critical')?.count || 0;
    const errCount   = bySev.find(s => s._id === 'error')?.count || 0;
    const warnCount  = bySev.find(s => s._id === 'warning')?.count || 0;

    return (
        <div className="sl-page">
            {/* ── Header ── */}
            <div className="sl-header">
                <div className="sl-header-left">
                    <div className="sl-header-icon">
                        <i className="fas fa-network-wired" />
                    </div>
                    <div>
                        <h1>Enterprise Syslog</h1>
                        <p>Real-time log management — MikroTik · Cisco · Fortinet · Linux · and more</p>
                    </div>
                </div>
                <div className="sl-header-right">
                    <DateInput type="datetime-local" className="sl-dt-input"
                        value={filters.startDate}
                        onChange={setFilter('startDate')} />
                    <span className="sl-dt-sep">→</span>
                    <DateInput type="datetime-local" className="sl-dt-input"
                        value={filters.endDate}
                        onChange={setFilter('endDate')} />
                    <button className="sl-refresh-btn" onClick={() => {
                        if (tab === 'dashboard') fetchDashboard();
                        else if (tab === 'logs') fetchLogs();
                        else if (tab === 'threats') fetchThreats();
                        else if (tab === 'alerts') fetchAlerts();
                    }}>
                        <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="sl-tabs">
                {[
                    { id: 'dashboard',   icon: 'fa-tachometer-alt',      label: 'Dashboard' },
                    { id: 'live',        icon: 'fa-broadcast-tower',      label: 'Live Stream' },
                    { id: 'logs',        icon: 'fa-scroll',               label: 'All Logs' },
                    { id: 'threats',     icon: 'fa-exclamation-triangle', label: 'Threats' },
                    { id: 'alerts',      icon: 'fa-bell',                 label: `Alerts${alerts.length ? ` (${alerts.length})` : ''}` },
                    { id: 'sessions',    icon: 'fa-user-clock',           label: 'Sessions' },
                    { id: 'browsing',    icon: 'fa-globe',                label: 'Browsing' },
                    { id: 'router-logs', icon: 'fa-server',               label: 'Router Logs' },
                ].map(t => (
                    <button key={t.id}
                        className={`sl-tab ${tab === t.id ? 'active' : ''}`}
                        onClick={() => setTab(t.id)}>
                        <i className={`fas ${t.icon}`} /> {t.label}
                    </button>
                ))}
            </div>

            {/* ════════════════════════════════════════════════
                TAB: DASHBOARD
            ═══════════════════════════════════════════════════ */}
            {tab === 'dashboard' && (
                <div className="sl-dashboard">
                    {/* Metric row */}
                    <div className="sl-metrics">
                        <MetricCard icon="fa-scroll"             label="Total Logs"    value={summary.totalLogs}  color="#6366f1" />
                        <MetricCard icon="fa-exclamation-circle" label="Critical"      value={critCount}          color="#dc2626" />
                        <MetricCard icon="fa-exclamation-triangle" label="Warnings"   value={warnCount}          color="#f59e0b" />
                        <MetricCard icon="fa-shield-alt"         label="Threats"       value={summary.threatCount}color="#7c3aed" />
                        <MetricCard icon="fa-bell"               label="Open Alerts"   value={summary.openAlerts} color="#ef4444" />
                    </div>

                    {/* Charts row */}
                    <div className="sl-charts">
                        <div className="sl-card">
                            <div className="sl-card-title"><i className="fas fa-chart-bar" /> Logs per Hour</div>
                            <Timeline data={dashboard?.hourlyTimeline} />
                            <div className="sl-timeline-legend">
                                <span className="sl-legend-dot" style={{ background: '#6366f1' }} /> Total
                                <span className="sl-legend-dot" style={{ background: '#dc2626', marginLeft: '1rem' }} /> Threats
                            </div>
                        </div>
                        <div className="sl-card">
                            <div className="sl-card-title"><i className="fas fa-tachometer-alt" /> By Severity</div>
                            <MiniBar items={bySev} total={summary.totalLogs} colorFn={sevColor} />
                        </div>
                        <div className="sl-card">
                            <div className="sl-card-title"><i className="fas fa-tags" /> By Category</div>
                            <MiniBar items={dashboard?.byCategory} total={summary.totalLogs} colorFn={() => '#6366f1'} />
                        </div>
                        <div className="sl-card">
                            <div className="sl-card-title"><i className="fas fa-router" /> Top Routers</div>
                            <MiniBar items={dashboard?.topRouters?.map(r => ({ _id: r.nasIdentifier || r._id, count: r.count }))}
                                total={summary.totalLogs} colorFn={() => '#10b981'} />
                        </div>
                        <div className="sl-card">
                            <div className="sl-card-title"><i className="fas fa-microchip" /> By Vendor</div>
                            <MiniBar items={dashboard?.byVendor} total={summary.totalLogs} colorFn={() => '#f59e0b'} />
                        </div>
                        <div className="sl-card">
                            <div className="sl-card-title"><i className="fas fa-exclamation-triangle" /> Recent Threats</div>
                            <div className="sl-threat-list">
                                {(dashboard?.recentThreats || []).slice(0, 8).map((t, i) => (
                                    <div key={i} className="sl-threat-row">
                                        <span className="sl-threat-type">{t.threatType}</span>
                                        <span className="sl-threat-ip">{t.srcIp || t.nasIp}</span>
                                        <span className="sl-threat-ago">{relTime(t.receivedAt)}</span>
                                    </div>
                                ))}
                                {!dashboard?.recentThreats?.length && <div className="sl-no-data">No threats detected</div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: LIVE STREAM
            ═══════════════════════════════════════════════════ */}
            {tab === 'live' && (
                <div className="sl-live">
                    <div className="sl-live-header">
                        <div className="sl-live-dot" />
                        <span>Live Stream — last 200 entries (auto-refresh 3s)</span>
                    </div>
                    <div className="sl-table-wrap">
                        <table className="sl-table">
                            <thead>
                                <tr>
                                    <th className="sl-sr">Sr. No.</th>
                                    <th>Time</th><th>Router</th><th>Hostname</th>
                                    <th>Vendor</th><th>Category</th><th>Severity</th>
                                    <th>Message</th><th>Src IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liveLogs.map((log, i) => <LogRow key={i} sr={i + 1} log={log} onSelect={setSelectedLog} />)}
                                {!liveLogs.length && (
                                    <tr><td colSpan={9} className="sl-empty">Waiting for syslog packets…</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: ALL LOGS (search + filter)
            ═══════════════════════════════════════════════════ */}
            {tab === 'logs' && (
                <div className="sl-logs">
                    {/* Filter bar */}
                    <div className="sl-filter-bar">
                        <input className="sl-filter-input" placeholder="Search message / IP / domain / user…"
                            value={filters.search} onChange={setFilter('search')} />
                        <input className="sl-filter-input sm" placeholder="NAS IP"
                            value={filters.nasIp} onChange={setFilter('nasIp')} />
                        <select className="sl-filter-select" value={filters.vendor} onChange={setFilter('vendor')}>
                            <option value="">All Vendors</option>
                            {['mikrotik','cisco','fortinet','juniper','ubiquiti','linux','windows','vmware','docker','pfsense','huawei','aruba','sophos','paloalto'].map(v => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                        <select className="sl-filter-select" value={filters.category} onChange={setFilter('category')}>
                            <option value="">All Categories</option>
                            {['firewall','nat','dns','dhcp','hotspot','radius','auth','vpn','ipsec','wireguard','system','interface','routing','wireless','qos','resource','general'].map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <select className="sl-filter-select" value={filters.severity} onChange={setFilter('severity')}>
                            <option value="">All Severities</option>
                            {['emergency','alert','critical','error','warning','notice','info','debug'].map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                        <select className="sl-filter-select sm" value={filters.isThreat} onChange={setFilter('isThreat')}>
                            <option value="">All</option>
                            <option value="true">Threats only</option>
                        </select>
                        <button className="sl-search-btn" onClick={() => fetchLogs(1)}>
                            <i className="fas fa-search" /> Search
                        </button>
                        <button type="button" className="sl-export-btn"
                            onClick={() => saveTable(syslogAPI.exportTable(filled(filters)), `syslog-${new Date().toISOString().slice(0, 10)}`, 'Syslog')}>
                            <i className="fas fa-file-excel" /> Excel
                        </button>
                    </div>

                    <div className="sl-table-info">
                        {pagination.total.toLocaleString()} entries found — page {pagination.page} / {pagination.pages}
                    </div>

                    <div className="sl-table-wrap">
                        <table className="sl-table">
                            <thead>
                                <tr>
                                    <th className="sl-sr">Sr. No.</th>
                                    <th>Time</th><th>Router</th><th>Hostname</th>
                                    <th>Vendor</th><th>Category</th><th>Severity</th>
                                    <th>Message</th><th>Src IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, i) => <LogRow key={i} sr={((pagination.page || 1) - 1) * 100 + i + 1} log={log} onSelect={setSelectedLog} />)}
                                {!logs.length && !loading && (
                                    <tr><td colSpan={9} className="sl-empty">No logs found for the selected filters</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="sl-pagination">
                            <button disabled={pagination.page <= 1} onClick={() => fetchLogs(pagination.page - 1)}>
                                <i className="fas fa-chevron-left" />
                            </button>
                            <span>Page {pagination.page} of {pagination.pages}</span>
                            <button disabled={pagination.page >= pagination.pages} onClick={() => fetchLogs(pagination.page + 1)}>
                                <i className="fas fa-chevron-right" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: THREATS
            ═══════════════════════════════════════════════════ */}
            {tab === 'threats' && (
                <div className="sl-threats-tab">
                    <div className="sl-threat-summary">
                        {threats.summary.map(s => (
                            <div key={s._id} className="sl-threat-summary-card">
                                <div className="sl-threat-summary-type">{s._id}</div>
                                <div className="sl-threat-summary-count">{s.count}</div>
                                <div className="sl-threat-summary-score">Avg score: {Math.round(s.avgScore)}</div>
                            </div>
                        ))}
                        {!threats.summary.length && <div className="sl-no-data">No threats in selected period</div>}
                    </div>
                    <div className="sl-table-wrap">
                        <table className="sl-table">
                            <thead>
                                <tr>
                                    <th className="sl-sr">Sr. No.</th>
                                    <th>Time</th><th>Router</th><th>Hostname</th>
                                    <th>Threat Type</th><th>Score</th><th>Severity</th>
                                    <th>Message</th><th>Src IP</th><th>Username</th>
                                </tr>
                            </thead>
                            <tbody>
                                {threats.logs.map((log, i) => (
                                    <tr key={i} className="sl-log-row threat" onClick={() => setSelectedLog(log)}>
                                        <td className="sl-sr">{i + 1}</td>
                                        <td className="sl-td-time">{fmtTime(log.receivedAt)}</td>
                                        <td className="sl-td-nas">{log.nasIp}</td>
                                        <td className="sl-td-host">{log.hostname || '—'}</td>
                                        <td><span className="sl-threat-chip"><i className="fas fa-exclamation-triangle" /> {log.threatType}</span></td>
                                        <td><span className="sl-score-badge" style={{ background: log.threatScore >= 80 ? '#fef2f2' : '#fffbeb', color: log.threatScore >= 80 ? '#dc2626' : '#b45309' }}>{log.threatScore}</span></td>
                                        <td><SevBadge s={log.severityName} /></td>
                                        <td className="sl-td-msg"><span className="sl-msg-text">{log.message}</span></td>
                                        <td className="sl-td-ip">{log.srcIp || '—'}</td>
                                        <td>{log.username || '—'}</td>
                                    </tr>
                                ))}
                                {!threats.logs.length && !loading && (
                                    <tr><td colSpan={10} className="sl-empty">No threats detected</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: ALERTS
            ═══════════════════════════════════════════════════ */}
            {tab === 'alerts' && (
                <div className="sl-alerts-tab">
                    {alerts.map(alert => (
                        <div key={alert._id} className={`sl-alert-card sev-${alert.severity}`}>
                            <div className="sl-alert-header">
                                <span className="sl-alert-title">
                                    <i className="fas fa-exclamation-triangle" /> {alert.title}
                                </span>
                                <div className="sl-alert-actions">
                                    <button className="sl-ack-btn" onClick={() => ackAlert(alert._id)}>Acknowledge</button>
                                    <button className="sl-resolve-btn" onClick={() => resolveAlert(alert._id)}>Resolve</button>
                                </div>
                            </div>
                            <div className="sl-alert-desc">{alert.description}</div>
                            <div className="sl-alert-meta">
                                <span><i className="fas fa-server" /> {alert.nasIp}</span>
                                <span><i className="fas fa-redo" /> {alert.count}x</span>
                                <span><i className="fas fa-clock" /> Last: {relTime(alert.lastSeen)}</span>
                                <span><i className="fas fa-history" /> First: {fmtTime(alert.firstSeen)}</span>
                                {alert.sourceIp && <span><i className="fas fa-network-wired" /> {alert.sourceIp}</span>}
                            </div>
                        </div>
                    ))}
                    {!alerts.length && <div className="sl-no-data">No open alerts</div>}
                </div>
            )}

            {/* ════════════════════════════════════════════════
                DoT SHARED FILTER BAR (Sessions / Browsing / Router Logs)
            ═══════════════════════════════════════════════════ */}
            {(tab === 'sessions' || tab === 'browsing' || tab === 'router-logs') && (
                <div className="sl-filter-bar">
                    <label className="sl-filter-label">From</label>
                    <DateInput type="date" className="sl-filter-input sm"
                        value={dotFilters.startDate}
                        onChange={setDotFilter('startDate')} />
                    <label className="sl-filter-label">To</label>
                    <DateInput type="date" className="sl-filter-input sm"
                        value={dotFilters.endDate}
                        onChange={setDotFilter('endDate')} />
                    <input className="sl-filter-input sm" placeholder="NAS IP (optional)"
                        value={dotFilters.nasIp}
                        onChange={setDotFilter('nasIp')} />
                    <button className="sl-search-btn" onClick={() => {
                        if (tab === 'sessions')    fetchDotSessions(1);
                        else if (tab === 'browsing')    fetchDotBrowsing(1);
                        else if (tab === 'router-logs') fetchDotRouterLogs(1);
                    }}>
                        <i className={`fas fa-search ${dotLoading ? 'fa-spin' : ''}`} /> Search
                    </button>
                    <button type="button" className="sl-export-btn" onClick={() => {
                        const type = tab === 'sessions' ? 'sessions' : tab === 'browsing' ? 'browsing' : 'router-system';
                        saveTable(dotLogsAPI.downloadTable(filled({ date: dotFilters.startDate, nasIp: dotFilters.nasIp, type })),
                            `dot-${type}-${dotFilters.startDate}${dotFilters.nasIp ? `-${dotFilters.nasIp}` : ''}`,
                            tab === 'sessions' ? 'Sessions' : tab === 'browsing' ? 'Browsing' : 'Router');
                    }}>
                        <i className="fas fa-file-excel" /> Excel
                    </button>
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: SESSIONS (DoT)
            ═══════════════════════════════════════════════════ */}
            {tab === 'sessions' && (
                <div className="sl-logs">
                    <div className="sl-table-info">
                        {dotPagSessions.total.toLocaleString()} session records
                    </div>
                    <DotTable rows={dotSessions} loading={dotLoading} firstSr={((dotPagSessions.page || 1) - 1) * 100 + 1} emptyMsg="No session records for the selected range" />
                    <DotPager pag={dotPagSessions} onPage={fetchDotSessions} />
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: BROWSING (DoT)
            ═══════════════════════════════════════════════════ */}
            {tab === 'browsing' && (
                <div className="sl-logs">
                    <div className="sl-table-info">
                        {dotPagBrowsing.total.toLocaleString()} browsing records
                    </div>
                    <DotTable rows={dotBrowsing} loading={dotLoading} firstSr={((dotPagBrowsing.page || 1) - 1) * 100 + 1} emptyMsg="No browsing records for the selected range" />
                    <DotPager pag={dotPagBrowsing} onPage={fetchDotBrowsing} />
                </div>
            )}

            {/* ════════════════════════════════════════════════
                TAB: ROUTER LOGS (DoT)
            ═══════════════════════════════════════════════════ */}
            {tab === 'router-logs' && (
                <div className="sl-logs">
                    <div className="sl-table-info">
                        {dotPagRouter.total.toLocaleString()} router log records
                    </div>
                    <DotTable rows={dotRouterLogs} loading={dotLoading} firstSr={((dotPagRouter.page || 1) - 1) * 100 + 1} emptyMsg="No router log records for the selected range" />
                    <DotPager pag={dotPagRouter} onPage={fetchDotRouterLogs} />
                </div>
            )}

            {/* ── Log detail modal ── */}
            <LogDetail log={selectedLog} onClose={() => setSelectedLog(null)} />
        </div>
    );
};

export default Syslog;
